import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { APIURL, TZ } from "../../../Global";
import './VendorRevenue.css';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { formatDateForFilename } from '../../../utils/dateUtils';

const VendorRevenue = () => {
  const [revenueData, setRevenueData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState('Today');
  const [selectedVendorType, setSelectedVendorType] = useState('All Vendors');
  const [searchVendorName, setSearchVendorName] = useState('');
  const [searchHospitalId, setSearchHospitalId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const scrollContainerRef = useRef(null);
  const { t } = useTranslation();
  const [exportOpen, setExportOpen] = useState(false);
  const navigate = useNavigate();

  // Summary stats
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalOrders, setTotalOrders] = useState(0);
  const [totalCommission, setTotalCommission] = useState(0);

  const menuTitleKeyMap = {
    'Time Period': 'revenue.timePeriod',
    'Vendor Type': 'revenue.vendorTypeMenu',
  };

  const submenuKeyMap = {
    'Today': 'revenue.today',
    'This Week': 'revenue.thisWeek',
    'This Month': 'revenue.thisMonth',
    'This Year': 'revenue.thisYear',
    'All Vendors': 'revenue.allVendors',
    'Hospital': 'revenue.vendorTypes.Hospital',
    'Clinic': 'revenue.vendorTypes.Clinic',
    'Laboratory': 'revenue.vendorTypes.Laboratory',
    'Pharmacy': 'revenue.vendorTypes.Pharmacy',
    'Diagnostic Center': 'revenue.vendorTypes.Diagnostic Center',
    'Therapists': 'revenue.vendorTypes.Therapists',
    'Neurodevelopmental Specialists': 'revenue.vendorTypes.Neurodevelopmental Specialists',
    'Dentists': 'revenue.vendorTypes.Dentists',
    'Odontologists': 'revenue.vendorTypes.Odontologists',
    'Patient Assistants': 'revenue.vendorTypes.Patient Assistants',
    'Psychologists': 'revenue.vendorTypes.Psychologists',
    'Nutritionists': 'revenue.vendorTypes.Nutritionists',
    'Physiotherapists': 'revenue.vendorTypes.Physiotherapists',
    'Respiratory Therapists': 'revenue.vendorTypes.Respiratory Therapists',
    'Electrocardiography': 'revenue.vendorTypes.Electrocardiography',
    'Speech Therapists': 'revenue.vendorTypes.Speech Therapists',
    'Single Doctor': 'revenue.vendorTypes.Single Doctor',
    'Single Nurse': 'revenue.vendorTypes.Single Nurse',
  };

  const menuData = [
    {
      title: 'Time Period',
      subMenus: ['Today', 'This Week', 'This Month', 'This Year'],
    },
    {
      title: 'Vendor Type',
      subMenus: [
        'All Vendors', 
        'Hospital', 
        'Clinic', 
        'Laboratory', 
        'Pharmacy', 
        'Diagnostic Center',
        'Therapists',
        'Neurodevelopmental Specialists',
        'Dentists',
        'Odontologists',
        'Patient Assistants',
        'Psychologists',
        'Nutritionists',
        'Physiotherapists',
        'Respiratory Therapists',
        'Electrocardiography',
        'Speech Therapists',
        'Single Doctor',
        'Single Nurse'
      ],
    },
  ];

  // Function to calculate dates based on selected period
  const getDateRange = (period) => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    let fromDate = new Date();

    switch (period) {
      case 'Today':
        fromDate.setHours(0, 0, 0, 0);
        break;
      case 'This Week':
        const dayOfWeek = today.getDay();
        const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        fromDate.setDate(today.getDate() - diffToMonday);
        fromDate.setHours(0, 0, 0, 0);
        break;
      case 'This Month':
        fromDate = new Date(today.getFullYear(), today.getMonth(), 1);
        fromDate.setHours(0, 0, 0, 0);
        break;
      case 'This Year':
        fromDate = new Date(today.getFullYear(), 0, 1);
        fromDate.setHours(0, 0, 0, 0);
        break;
      default:
        return { fromDate: dateFrom, toDate: dateTo };
    }

    return {
      fromDate: fromDate.toISOString().split('T')[0],
      toDate: today.toISOString().split('T')[0]
    };
  };

  // Initialize dates on mount
  useEffect(() => {
    const { fromDate, toDate } = getDateRange('Today');
    setDateFrom(fromDate);
    setDateTo(toDate);
  }, []);

  useEffect(() => {
    if (dateFrom && dateTo) {
      fetchRevenueData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMenu, selectedVendorType, searchVendorName, searchHospitalId, dateFrom, dateTo]);

  const fetchRevenueData = async () => {
    setLoading(true);
    const tokenString = sessionStorage.getItem('usertoken');
    let str = tokenString?.replace(/["]+/g, '');

    // Build query params dynamically
    let queryParams = `start_date=${dateFrom}&end_date=${dateTo}&${TZ}`;

    // Add vendor name search
    if (searchVendorName && searchVendorName.trim()) {
      queryParams += `&hospital_name=${encodeURIComponent(searchVendorName.trim())}`;
    }

    // Add hospital ID search
    if (searchHospitalId && searchHospitalId.trim()) {
      queryParams += `&hospital_id=${encodeURIComponent(searchHospitalId.trim())}`;
    }

    if (selectedVendorType && selectedVendorType !== 'All Vendors') {
      queryParams += `&sp_type=${encodeURIComponent(selectedVendorType)}`;
    }

    try {
      const res = await axios.get(
        `${APIURL}/api/v1/staff/orders-commissions/?${queryParams}`,
        {
          headers: {
            Authorization: 'Token ' + str,
          },
        }
      );

      console.log('API Response:', res.data); // Debug log

      if (res.data.status === 'success') {
        // Use the summary data directly from the API response
        setTotalRevenue(res.data.all_revenue || 0);
        setTotalOrders(res.data.all_orders || 0);
        setTotalCommission(res.data.commission_received || 0);

        // Process the detail data for the table
        if (res.data.detail) {
          const processedData = res.data.detail.map((item, index) => ({
            id: item.hospital_id || index + 1,
            hospital_id: item.hospital_id,
            vendor_name: item.vendor_name,
            total_orders: item.total_orders,
            revenue: item.total_revenue,
            commission_amount: item.commission_amount,
            commission_percent: parseFloat(item.commission_percent) || 0,
            net_payout: item.payout,
            subscription: item.plan_period ?
              item.plan_period.charAt(0).toUpperCase() + item.plan_period.slice(1) : '',
            period: item.plan_start_date && item.plan_end_date
              ? `${formatDateDisplay(item.plan_start_date)} - ${formatDateDisplay(item.plan_end_date)}`
              : '',
          }));

          console.log('Processed Data:', processedData); // Debug log
          setRevenueData(processedData);
        } else {
          setRevenueData([]);
        }
      } else {
        console.log('No data in response');
        setRevenueData([]);
        setTotalRevenue(0);
        setTotalOrders(0);
        setTotalCommission(0);
      }
    } catch (err) {
      console.error('Error fetching revenue data:', err);
      setRevenueData([]);
      setTotalRevenue(0);
      setTotalOrders(0);
      setTotalCommission(0);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to format dates for display
  const formatDateDisplay = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toISOString().split('T')[0];
    } catch (e) {
      return 'Invalid Date';
    }
  };

  const handleSubMenuClick = (subMenu, menuTitle) => {
    if (menuTitle === 'Time Period') {
      setSelectedMenu(subMenu);
      const { fromDate, toDate } = getDateRange(subMenu);
      setDateFrom(fromDate);
      setDateTo(toDate);
    } else if (menuTitle === 'Vendor Type') {
      setSelectedVendorType(subMenu);
    }
  };

  const handleDateFromChange = (e) => {
    setDateFrom(e.target.value);
    setSelectedMenu('Custom');
  };

  const handleDateToChange = (e) => {
    setDateTo(e.target.value);
    setSelectedMenu('Custom');
  };

  const renderPieChart = () => {
    const data = [
      { label: t('revenue.totalRevenue'), value: totalRevenue, color: '#0DA5E9' },
      { label: t('revenue.totalOrders'), value: totalOrders, color: '#3E3C8D' },
      { label: t('revenue.commissionReceived'), value: totalCommission, color: '#6CEAC0' },
    ];

    const total = data.reduce((sum, item) => sum + item.value, 0);

    // If all values are 0, show a message
    if (total === 0) {
      return (
        <div className="pie-chart-section">
          <div className="pie-chart-header">{t('revenue.pieTitle')}</div>
          <div className="pie-chart-container row">
            <div className="pie-legend col-12">
              <div className="text-center py-3">
                {t('revenue.noData')}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="pie-chart-section">
        <div className="pie-chart-header">{t('revenue.pieTitle')}</div>
        <div className="pie-chart-container row">
          <div className="pie-legend col-6">
            {data.map((item, index) => (
              <div key={index} className="pie-legend-item">
                <div className="pie-legend-color" style={{ background: item.color }}></div>
                <div className="pie-legend-details">
                  <div className="pie-legend-label">{item.label}:</div>
                  <div className="pie-legend-value">{formatCurrency(item.value)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Format numbers with 2 decimal points and CRC symbol
  const formatNumber = (number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(number);
  };

  // Format currency for table with CRC symbol
  const formatCurrency = (amount) => {
    return `₡${formatNumber(amount)}`;
  };

  const handleRowClick = (item) => {
    const id = item?.hospital_id || item?.id;
    if (id) {
      const encodedId = encodeURIComponent(id);
      navigate(`/vendor-revenue/${encodedId}`, {
        state: {
          vendorName: item.vendor_name
        }
      });
    }
  };

  const renderContent = () => {
    return (
      <div>
        {/* Revenue Summary Section */}
        <div className="revenue-summary-section">
          <div className="revenue-summary-left">
            <h2 className="revenue-summary-title">{t('revenue.summaryTitle')}</h2>

            {/* Search and Filters */}
            <div className="revenue-filters">
              <div className="search-filters-row">
                <input
                  type="text"
                  className="search-input1"
                  placeholder={t('revenue.searchVendorNamePlaceholder')}
                  value={searchVendorName}
                  onChange={(e) => setSearchVendorName(e.target.value)}
                />
                <input
                  type="text"
                  className="search-input1"
                  placeholder={t('revenue.searchHospitalIdPlaceholder')}
                  value={searchHospitalId}
                  onChange={(e) => setSearchHospitalId(e.target.value)}
                />
              </div>
              <div className="date-filters" style={{ width: '100%' }}>
                <div className="date-filter-group">
                  <label>{t('revenue.fromDate')}</label>
                  <input
                    type="date"
                    className="search-input1"
                    value={dateFrom}
                    onChange={handleDateFromChange}
                  />
                </div>
                <div className="date-filter-group">
                  <label>{t('revenue.toDate')}</label>
                  <input
                    type="date"
                    className="search-input1"
                    value={dateTo}
                    onChange={handleDateToChange}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Pie Chart */}
          <div className="revenue-summary-right" style={{ width: '40%' }}>
            {renderPieChart()}
          </div>
        </div>

        {/* Revenue Data Header */}
        <div className="revenue-data-header">
          <h3 className="revenue-data-title">{t('revenue.dataTitle')}</h3>
          <div className="revenue-data-actions">
            <div className="export-dropdown">
              <button className="export-btn" onClick={() => setExportOpen(v => !v)}>
                <svg viewBox="0 0 24 24" fill="white" style={{ marginRight: '6px' }}>
                  <path d="M19 12v7H5v-7H3v7c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2zm-6 .67l2.59-2.58L17 11.5l-5 5-5-5 1.41-1.41L11 12.67V3h2z" />
                </svg>
                {t('revenue.export')} ▼
              </button>
              {exportOpen && (
                <div className="export-menu">
                  <button onClick={() => { handleExportCSV(); setExportOpen(false); }}>{t('revenue.exportCSV')}</button>
                  <button onClick={() => { handleExportPDF(); setExportOpen(false); }}>{t('revenue.exportPDF')}</button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Revenue Table - Adjusted for 6 columns with hospital_id as first column */}
        <div className="revenue-table-container" ref={scrollContainerRef}>
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">{t('revenue.loading')}</span>
              </div>
            </div>
          ) : revenueData.length === 0 ? (
            <div className="no-data">{t('revenue.noData')}</div>
          ) : (
            <>
              <div className="revenue-table-header revenue-grid-6">
                <div><b>{t('revenue.headers.hospitalId')}</b></div>
                <div><b>{t('revenue.headers.vendorName')}</b></div>
                <div><b>{t('revenue.headers.totalOrders')}</b></div>
                <div><b>{t('revenue.headers.revenue')}</b></div>
                <div><b>{t('revenue.headers.commissionAmountPercent')}</b></div>
                <div><b>{t('revenue.headers.netPayout')}</b></div>
              </div>
              {revenueData.map((item) => (
                <div
                  className="revenue-table-row revenue-grid-6"
                  key={item.id}
                  onClick={() => handleRowClick(item)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="truncate" style={{ fontWeight: '600' }}>
                    {item.hospital_id}
                  </div>
                  <div className="truncate" style={{ fontWeight: '600' }}>
                    {item.vendor_name}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span className="num" >{item.total_orders}</span>&nbsp;
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="#9aa6ac" style={{ marginRight: 2 }}>
                      <path d="M3 3h2v18H3V3zm4 6h2v12H7V9zm4-4h2v16h-2V5zm4 8h2v8h-2v-8zm4-6h2v14h-2V7z" />
                    </svg>
                  </div>
                  <div className="num" style={{ color: '#1ec6b6', fontWeight: '600' }}>
                    {formatCurrency(item.revenue)}
                  </div>
                  <div className="num">
                    <span className="commission-text">
                      {formatCurrency(item.commission_amount)}
                    </span>
                    <span className={`commission-percent ${item.commission_percent > 10 ? 'negative' : 'positive'}`}>
                      {item.commission_percent > 10 ? '-' : ''}{item.commission_percent}%
                    </span>
                  </div>
                  <div className="num" style={{ fontWeight: '600' }}>
                    {formatCurrency(item.net_payout)}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    );
  };

  const handleExportCSV = () => {
    if (!revenueData || revenueData.length === 0) return;
    const header = [
      t('revenue.headers.hospitalId'),
      t('revenue.headers.vendorName'),
      t('revenue.headers.totalOrders'),
      t('revenue.headers.revenue'),
      t('revenue.headers.commissionAmount'),
      t('revenue.headers.commissionPercent'),
      t('revenue.headers.netPayout'),
    ];
    const rows = revenueData.map(item => [
      sanitizeCSV(item.hospital_id),
      sanitizeCSV(item.vendor_name),
      item.total_orders,
      item.revenue,
      item.commission_amount,
      item.commission_percent,
      item.net_payout,
    ]);
    const csvContent = [header, ...rows]
      .map(r => r.map(formatCSVField).join(','))
      .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `revenue_${formatDateForFilename()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatCSVField = (value) => {
    if (value === null || value === undefined) return '';
    const str = String(value).replace(/"/g, '""');
    if (/[",\n]/.test(str)) return `"${str}"`;
    return str;
  };

  const sanitizeCSV = (value) => {
    if (value === null || value === undefined) return '';
    const str = String(value);
    return /^[=+\-@]/.test(str) ? `'${str}` : str;
  };

  const handleExportPDF = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const style = `
      <style>
        body { font-family: Arial, sans-serif; padding: 16px; }
        h1 { font-size: 18px; margin: 0 0 12px; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; }
        th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; }
        th { background: #f0f0f0; }
      </style>
    `;
    const headers = [
      t('revenue.headers.hospitalId'),
      t('revenue.headers.vendorName'),
      t('revenue.headers.totalOrders'),
      t('revenue.headers.revenue'),
      t('revenue.headers.commissionAmount'),
      t('revenue.headers.commissionPercent'),
      t('revenue.headers.netPayout'),
    ];
    const rows = revenueData.map(item => `
      <tr>
        <td>${escapeHTML(item.hospital_id)}</td>
        <td>${escapeHTML(item.vendor_name)}</td>
        <td>${item.total_orders}</td>
        <td>${formatCurrency(item.revenue)}</td>
        <td>${formatCurrency(item.commission_amount)}</td>
        <td>${item.commission_percent}%</td>
        <td>${formatCurrency(item.net_payout)}</td>
      </tr>
    `).join('');
    const thead = `<tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr>`;
    const html = `
      <html>
        <head>
          <title>${t('revenue.title')}</title>
          ${style}
        </head>
        <body>
          <h1>${t('revenue.dataTitle')}</h1>
          <table>
            <thead>${thead}</thead>
            <tbody>${rows}</tbody>
          </table>
        </body>
      </html>
    `;
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const escapeHTML = (str) => {
    if (str === null || str === undefined) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  return (
    <div style={{ background: '#f6fafd', width: '100%', overflowX: 'hidden' }}>
      <div className="revenue-layout">
        <div className="side-menu-revenue">
          <div className="menu-header">{t('revenue.title')}</div>
          {menuData.map((menu) => (
            <div key={menu.title}>
              <div className="menu-title-revenue">{t(menuTitleKeyMap[menu.title] || menu.title)}</div>
              <div className="sub-menu-list-revenue">
                {menu.subMenus.map((sub) => {
                  const isActive =
                    menu.title === 'Time Period'
                      ? selectedMenu === sub
                      : selectedVendorType === sub;
                  return (
                    <div
                      key={sub}
                      className={`sub-menu-revenue${isActive ? ' active' : ''}`}
                      onClick={() => handleSubMenuClick(sub, menu.title)}
                    >
                      {t(submenuKeyMap[sub] || sub)}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div className="right-content-revenue">{renderContent()}</div>
      </div>
    </div>
  );
};

export default VendorRevenue;